import {
  filterScoringPlays,
  getCurrentHalfInning,
  getCurrentInningNumber,
  getCurrentOuts,
  type MlbGameDataResponse,
  type MlbPlayByPlayResponse,
  type GameraPlayerReference,
  type StatModel,
  getCurrentAtBat,
  getCurrentAtBatCount,
  getCurrentRunners,
  type InningPlayByPlay,
  type PlayerGameModel,
  type MlbStatKey,
  type InningAtBatEvent,
  getValue,
  type MlbBoxGameState,
} from '@statmuse/core/gamera'
import { atom, map, computed } from 'nanostores'
import { last } from 'lodash-es'

interface GameState extends Record<string, unknown> {
  gameData?: MlbGameDataResponse<MlbStatKey>
  playByPlay?: MlbPlayByPlayResponse
}

export const gameState = map<GameState>({})

export const innings = atom<InningPlayByPlay[]>([])

export const scoringInnings = computed(innings, (i) => filterScoringPlays(i))

export const outs = computed(innings, (i) => getCurrentOuts(i))

export const gameScore = map({ home: 0, away: 0 })

export const halfInning = computed(innings, (i) => getCurrentHalfInning(i))

export const inningNumber = computed(innings, (i) => getCurrentInningNumber(i))

export const players = map<Record<number, GameraPlayerReference | undefined>>(
  {},
)

export const atBat = computed(innings, (i) => getCurrentAtBat(i))

export const atBatCount = computed(innings, (i) => getCurrentAtBatCount(i))

export const runners = computed(innings, (i) => getCurrentRunners(i))

export const lineup = map<{
  home?: PlayerGameModel[]
  away?: PlayerGameModel[]
}>({})

export const stats = map<{
  away?: {
    stats?: StatModel<MlbStatKey>
    splits?: {
      splitType?: string
      playerId: number
      stats?: StatModel<MlbStatKey>
    }[]
  }
  home?: {
    stats?: StatModel<MlbStatKey>
    splits?: {
      splitType?: string
      playerId: number
      stats?: StatModel<MlbStatKey>
    }[]
  }
}>({})

export const lineScore = map<{
  away?: {
    inning: number
    runs: number
  }[]
  home?: {
    inning: number
    runs: number
  }[]
}>({})

export const matchup = computed(
  [lineup, stats, atBat, halfInning, players],
  (line, s, ab, hi, p) => {
    const awayAbMatchup = hi?.half == 'top' ? ab?.batter : ab?.pitcher
    const homeAbMatchup = hi?.half === 'bottom' ? ab?.batter : ab?.pitcher

    return {
      away: {
        type: hi?.half == 'top' ? 'batter' : 'pitcher',
        player: p?.[awayAbMatchup?.playerId ?? 0],
        handedness: awayAbMatchup?.handedness,
        position: last(
          line.away?.find((x) => x.playerId === awayAbMatchup?.playerId)?.lineup
            .positions,
        ),
        stats: s.away?.splits?.find(
          (s) => s.playerId === awayAbMatchup?.playerId,
        )?.stats,
      },
      home: {
        type: hi?.half == 'bottom' ? 'batter' : 'pitcher',
        player: p?.[homeAbMatchup?.playerId ?? 0],
        handedness: homeAbMatchup?.handedness,
        position: last(
          line.home?.find((x) => x.playerId === homeAbMatchup?.playerId)?.lineup
            .positions,
        ),
        stats: s.home?.splits?.find(
          (s) => s.playerId === homeAbMatchup?.playerId,
        )?.stats,
      },
    }
  },
)

export const battingSummary = computed([stats, players], ($stats, $players) => {
  const awaySplits = $stats.away?.splits
  const homeSplits = $stats.home?.splits

  const awayDoubles = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-Doubles') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-Doubles'),
      }
    })
  const homeDoubles = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-Doubles') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-Doubles'),
      }
    })

  const awayTriples = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-Triples') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-Triples'),
      }
    })
  const homeTriples = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-Triples') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-Triples'),
      }
    })

  const awayHomeRuns = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-HomeRuns') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-HomeRuns'),
      }
    })
  const homeHomeRuns = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-HomeRuns') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-HomeRuns'),
      }
    })

  const awayTotalBases = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-TotalBases') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-TotalBases'),
      }
    })
  const homeTotalBases = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-TotalBases') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-TotalBases'),
      }
    })

  const awayRbis = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-RunsBattedIn') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-RunsBattedIn'),
      }
    })
  const homeRbis = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-RunsBattedIn') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-RunsBattedIn'),
      }
    })

  const awayTwoOutRbis = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-TwoOutRunsBattedIn') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-TwoOutRunsBattedIn'),
      }
    })
  const homeTwoOutRbis = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-TwoOutRunsBattedIn') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-TwoOutRunsBattedIn'),
      }
    })

  const awayTwoOutRunnersLeft = awaySplits
    ?.filter(
      (s) =>
        getValue(s.stats ?? {}, 'Batting-TwoOutRunnersLeftInScoringPosition') >
        0,
    )
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(
          s.stats ?? {},
          'Batting-TwoOutRunnersLeftInScoringPosition',
        ),
      }
    })
  const homeTwoOutRunnersLeft = homeSplits
    ?.filter(
      (s) =>
        getValue(s.stats ?? {}, 'Batting-TwoOutRunnersLeftInScoringPosition') >
        0,
    )
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(
          s.stats ?? {},
          'Batting-TwoOutRunnersLeftInScoringPosition',
        ),
      }
    })

  const awaySacBunts = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-SacrificeHits') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-SacrificeHits'),
      }
    })
  const homeSacBunts = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-SacrificeHits') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-SacrificeHits'),
      }
    })

  const awaySacFlies = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-SacrificeFlies') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-SacrificeFlies'),
      }
    })
  const homeSacFlies = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-SacrificeFlies') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-SacrificeFlies'),
      }
    })

  const awayGidp = awaySplits
    ?.filter(
      (s) => getValue(s.stats ?? {}, 'Batting-GroundedIntoDoublePlays') > 0,
    )
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-GroundedIntoDoublePlays'),
      }
    })
  const homeGidp = homeSplits
    ?.filter(
      (s) => getValue(s.stats ?? {}, 'Batting-GroundedIntoDoublePlays') > 0,
    )
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-GroundedIntoDoublePlays'),
      }
    })

  const awayIbb = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-IntentionalWalks') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-IntentionalWalks'),
      }
    })
  const homeIbb = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-IntentionalWalks') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-IntentionalWalks'),
      }
    })

  const awayHbp = awaySplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-HitByPitches') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-HitByPitches'),
      }
    })
  const homeHbp = homeSplits
    ?.filter((s) => getValue(s.stats ?? {}, 'Batting-HitByPitches') > 0)
    .map((s) => {
      const player = $players[s.playerId]
      return {
        name: player?.entity.shortDisplay ?? player?.entity.display,
        total: getValue(s.stats ?? {}, 'Batting-HitByPitches'),
      }
    })

  const awayTeamRisp = getValue(
    $stats.away?.stats ?? {},
    'Batting-AtBatsWithRunnersInScoringPosition',
  )
  const homeTeamRisp = getValue(
    $stats.home?.stats ?? {},
    'Batting-AtBatsWithRunnersInScoringPosition',
  )

  const awayTeamHitsRisp = getValue(
    $stats.away?.stats ?? {},
    'Batting-HitsWithRunnersInScoringPosition',
  )
  const homeTeamHitsRisp = getValue(
    $stats.home?.stats ?? {},
    'Batting-HitsWithRunnersInScoringPosition',
  )

  const awayTeamLob = getValue(
    $stats.away?.stats ?? {},
    'Batting-LeftOnBaseAtEndOfInning',
  )
  const homeTeamLob = getValue(
    $stats.home?.stats ?? {},
    'Batting-LeftOnBaseAtEndOfInning',
  )

  return {
    away: {
      doubles: awayDoubles?.length > 0 ? awayDoubles : undefined,
      triples: awayTriples?.length > 0 ? awayTriples : undefined,
      homeRuns: awayHomeRuns?.length > 0 ? awayHomeRuns : undefined,
      rbis: awayRbis?.length > 0 ? awayRbis : undefined,
      totalBases: awayTotalBases?.length > 0 ? awayTotalBases : undefined,
      twoOutRbis: awayTwoOutRbis?.length > 0 ? awayTwoOutRbis : undefined,
      twoOutRunnersLeft:
        awayTwoOutRunnersLeft?.length > 0 ? awayTwoOutRunnersLeft : undefined,
      sacBunts: awaySacBunts?.length > 0 ? awaySacBunts : undefined,
      sacFlies: awaySacFlies?.length > 0 ? awaySacFlies : undefined,
      gidp: awayGidp?.length > 0 ? awayGidp : undefined,
      ibb: awayIbb?.length > 0 ? awayIbb : undefined,
      hbp: awayHbp?.length > 0 ? awayHbp : undefined,
      risp: awayTeamRisp,
      hitsRisp: awayTeamHitsRisp,
      lob: awayTeamLob,
    },
    home: {
      doubles: homeDoubles?.length > 0 ? homeDoubles : undefined,
      triples: homeTriples?.length > 0 ? homeTriples : undefined,
      homeRuns: homeHomeRuns?.length > 0 ? homeHomeRuns : undefined,
      rbis: homeRbis?.length > 0 ? homeRbis : undefined,
      totalBases: homeTotalBases?.length > 0 ? homeTotalBases : undefined,
      twoOutRbis: homeTwoOutRbis?.length > 0 ? homeTwoOutRbis : undefined,
      twoOutRunnersLeft:
        homeTwoOutRunnersLeft?.length > 0 ? homeTwoOutRunnersLeft : undefined,
      sacBunts: homeSacBunts?.length > 0 ? homeSacBunts : undefined,
      sacFlies: homeSacFlies?.length > 0 ? homeSacFlies : undefined,
      gidp: homeGidp?.length > 0 ? homeGidp : undefined,
      ibb: homeIbb?.length > 0 ? homeIbb : undefined,
      hbp: homeHbp?.length > 0 ? homeHbp : undefined,
      risp: homeTeamRisp,
      hitsRisp: homeTeamHitsRisp,
      lob: homeTeamLob,
    },
  }
})

export const pitchingSummary = computed(
  [stats, players],
  ($stats, $players) => {
    const awaySplits = $stats.away?.splits
    const homeSplits = $stats.home?.splits

    const awayBattersFaced = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-TotalBattersFaced') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-TotalBattersFaced'),
        }
      })
    const homeBattersFaced = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-TotalBattersFaced') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-TotalBattersFaced'),
        }
      })

    const awayWp = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-WildPitches') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-WildPitches'),
        }
      })
    const homeWp = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-WildPitches') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-WildPitches'),
        }
      })

    const awayBalks = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-Balks') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-Balks'),
        }
      })
    const homeBalks = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-Balks') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-Balks'),
        }
      })

    const awayPitches = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-PitchesThrown') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-PitchesThrown'),
          strikes: getValue(s.stats ?? {}, 'Pitching-StrikesThrown'),
        }
      })
    const homePitches = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-PitchesThrown') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Pitching-PitchesThrown'),
          strikes: getValue(s.stats ?? {}, 'Pitching-StrikesThrown'),
        }
      })

    const awayOuts = awaySplits
      ?.filter(
        (s) =>
          getValue(s.stats ?? {}, 'Pitching-GroundOuts') > 0 ||
          getValue(s.stats ?? {}, 'Pitching-FlyOuts') > 0,
      )
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          go: getValue(s.stats ?? {}, 'Pitching-GroundOuts'),
          fo: getValue(s.stats ?? {}, 'Pitching-FlyOuts'),
        }
      })
    const homeOuts = homeSplits
      ?.filter(
        (s) =>
          getValue(s.stats ?? {}, 'Pitching-GroundOuts') > 0 ||
          getValue(s.stats ?? {}, 'Pitching-FlyOuts') > 0,
      )
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          go: getValue(s.stats ?? {}, 'Pitching-GroundOuts'),
          fo: getValue(s.stats ?? {}, 'Pitching-FlyOuts'),
        }
      })

    const awayInherited = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-InheritedRunners') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          runners: getValue(s.stats ?? {}, 'Pitching-InheritedRunners'),
          scored: getValue(s.stats ?? {}, 'Pitching-InheritedRunnersScored'),
        }
      })
    const homeInherited = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Pitching-InheritedRunners') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          runners: getValue(s.stats ?? {}, 'Pitching-InheritedRunners'),
          scored: getValue(s.stats ?? {}, 'Pitching-InheritedRunnersScored'),
        }
      })

    return {
      away: {
        bf: awayBattersFaced?.length > 0 ? awayBattersFaced : undefined,
        wp: awayWp?.length > 0 ? awayWp : undefined,
        balks: awayBalks?.length > 0 ? awayBalks : undefined,
        pitches: awayPitches?.length > 0 ? awayPitches : undefined,
        outs: awayOuts?.length > 0 ? awayOuts : undefined,
        inherited: awayInherited?.length > 0 ? awayInherited : undefined,
      },
      home: {
        bf: homeBattersFaced?.length > 0 ? homeBattersFaced : undefined,
        wp: homeWp?.length > 0 ? homeWp : undefined,
        balks: homeBalks?.length > 0 ? homeBalks : undefined,
        pitches: homePitches?.length > 0 ? homePitches : undefined,
        outs: homeOuts?.length > 0 ? homeOuts : undefined,
        inherited: homeInherited?.length > 0 ? homeInherited : undefined,
      },
    }
  },
)

export const baseRunningSummary = computed(
  [stats, players],
  ($stats, $players) => {
    const awaySplits = $stats.away?.splits
    const homeSplits = $stats.home?.splits

    const awaySB = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Batting-StolenBases') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Batting-StolenBases'),
        }
      })
    const homeSB = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Batting-StolenBases') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Batting-StolenBases'),
        }
      })

    const awayCS = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Batting-CaughtStealing') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Batting-CaughtStealing'),
        }
      })
    const homeCS = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Batting-CaughtStealing') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Batting-CaughtStealing'),
        }
      })

    return {
      away: {
        stolenBases: awaySB?.length > 0 ? awaySB : undefined,
        caughtStealing: awayCS?.length > 0 ? awayCS : undefined,
      },
      home: {
        stolenBases: homeSB?.length > 0 ? homeSB : undefined,
        caughtStealing: homeCS?.length > 0 ? homeCS : undefined,
      },
    }
  },
)

export const fieldingSummary = computed(
  [stats, players],
  ($stats, $players) => {
    const awaySplits = $stats.away?.splits
    const homeSplits = $stats.home?.splits

    const awayErrors = awaySplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Fielding-Errors') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Fielding-Errors'),
        }
      })
    const homeErrors = homeSplits
      ?.filter((s) => getValue(s.stats ?? {}, 'Fielding-Errors') > 0)
      .map((s) => {
        const player = $players[s.playerId]
        return {
          name: player?.entity.shortDisplay ?? player?.entity.display,
          total: getValue(s.stats ?? {}, 'Fielding-Errors'),
        }
      })

    return {
      away: {
        errors: awayErrors?.length > 0 ? awayErrors : undefined,
      },
      home: {
        errors: homeErrors?.length > 0 ? homeErrors : undefined,
      },
    }
  },
)

export const selectedId = atom<
  | { id: number; alignment: 'home' | 'away'; type: 'pitcher' | 'batter' }
  | undefined
>()

export const selectedPlayer = computed(
  [selectedId, players, stats, innings, gameState],
  ($selectedId, $players, $stats, $innings, $gameState) => {
    if (!$selectedId) return undefined

    const { alignment, type } = $selectedId
    const player = $players[$selectedId.id]

    const oppTeamId =
      alignment === 'home'
        ? $gameState.gameData?.awayTeam.teamId
        : $gameState.gameData?.homeTeam.teamId
    const oppTeam = $gameState.gameData?.teams?.find(
      (t) => t.teamId === oppTeamId,
    )

    const stats = $stats[alignment]?.splits?.find(
      (s) => s.playerId === player?.id,
    )?.stats

    const atBats = $innings.flatMap((inning) => {
      const number = inning.number
      const half = inning.halves[alignment === 'away' ? 0 : 1].half

      return inning.halves[alignment === 'away' ? 0 : 1].events
        .filter((e) => e.type === 'atBat')
        .filter((e) => e.batter.playerId === player?.id)
        .map((x) => ({
          ...x,
          number,
          half,
          pitcher: $players[x.pitcher.playerId],
        }))
    })

    return {
      type,
      player,
      stats,
      oppTeam,
      alignment,
      gameTimestamp:
        $gameState.gameData?.gameTimestamp ?? $gameState.gameData?.gameDate,
      atBats,
    }
  },
)

export const selectedAtBat = atom<
  | {
      atBat: InningAtBatEvent
      number: number
      half: string
      pitcher?: GameraPlayerReference
    }
  | undefined
>()

export const init = (props: {
  gameData: MlbGameDataResponse<MlbStatKey>
  playByPlay?: MlbPlayByPlayResponse
}) => {
  const { gameData, playByPlay } = props

  gameState.set({ gameData, playByPlay })
  innings.set(playByPlay?.innings ?? [])
  gameScore.set({
    away: gameData.awayTeam.score ?? 0,
    home: gameData.homeTeam.score ?? 0,
  })
  lineScore.set({
    away: gameData.awayTeam.lineScore,
    home: gameData.homeTeam.lineScore,
  })
  stats.set({
    away: gameData.awayTeam.stats,
    home: gameData.homeTeam.stats,
  })
  lineup.set({
    away: gameData.awayTeam.players,
    home: gameData.homeTeam.players,
  })
  players.set(
    gameData.players?.reduce((acc, p) => ({ ...acc, [p.id]: p }), {}) ?? {},
  )
}

export const update = (data: MlbBoxGameState) => {
  gameScore.set(data.gameScore)
  lineScore.set(data.lineScore)
  lineup.set(data.lineup)
  stats.set(data.stats)
  players.set(
    data.players?.reduce((acc, p) => ({ ...acc, [p.id]: p }), {}) ?? {},
  )

  const lastInning = data.lastInning
  if (lastInning) {
    innings.set([
      ...innings.get().filter((i) => i.number !== lastInning.number),
      lastInning,
    ])
  }
}
